// frontend/src/components/common/Modal.jsx
import React, { useEffect, useRef } from 'react';
import './Modal.css'; // You will create this CSS file for styling
// import Icon from './Icon'; // For a custom close icon if not using ×

/**
 * A reusable Modal component with overlay and basic accessibility features.
 * @param {object} props
 * @param {boolean} props.isOpen - Controls whether the modal is visible.
 * @param {function} props.onClose - Callback function to close the modal.
 * @param {React.ReactNode} props.children - Content to be rendered inside the modal.
 * @param {string} [props.title] - Optional title for the modal, used for aria-labelledby if titleId is also provided.
 * @param {string} [props.titleId] - Optional ID for the modal's title element (for aria-labelledby).
 * @param {string} [props.descriptionId] - Optional ID for the modal's description element (for aria-describedby).
 * @param {string} [props.className=''] - Additional class name for the modal-content div.
 * @param {string} [props.overlayClassName=''] - Additional class name for the modal-overlay div.
 * @param {boolean} [props.closeOnOverlayClick=true] - Whether clicking the overlay closes the modal.
 * @param {boolean} [props.showCloseButton=true] - Whether to show the default 'X' close button.
 */
const Modal = ({ 
  isOpen, 
  onClose, 
  children, 
  title,
  titleId,      // e.g., "my-modal-title"
  descriptionId,// e.g., "my-modal-description"
  className = '',
  overlayClassName = '',
  closeOnOverlayClick = true,
  showCloseButton = true
}) => {
  const modalRef = useRef(null); // Ref for the modal content itself
  const previousActiveElement = useRef(null); // To store element that had focus before modal opened

  // Handle Escape key to close modal
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      // Store the element that had focus before modal opened
      previousActiveElement.current = document.activeElement;
      // TODO: Implement focus trapping within the modal
      // For now, try to focus the modal itself or its first focusable element
      modalRef.current?.focus(); // Needs tabIndex="-1" on modal-content for this to work programmatically
    } else if (previousActiveElement.current) {
      // Return focus to the element that previously had it when modal closes
      previousActiveElement.current.focus();
      previousActiveElement.current = null; // Clear ref
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { // Cleanup on unmount
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);


  if (!isOpen) {
    return null;
  }

  const handleOverlayClick = () => {
    if (closeOnOverlayClick) {
      onClose();
    }
  };

  const handleContentClick = (e) => {
    e.stopPropagation(); // Prevent modal from closing when clicking inside its content area
  };

  return (
    <div 
      className={`modal-overlay ${overlayClassName}`} // Styled by Modal.css
      onClick={handleOverlayClick}
      role="presentation" // Overlay itself is not interactive beyond closing
    >
      <div
        ref={modalRef}
        className={`modal-content ${className}`} // Styled by Modal.css
        onClick={handleContentClick}
        role="dialog" // ARIA role for the modal dialog
        aria-modal="true" // Indicates it's a modal dialog, trapping focus (conceptually)
        aria-labelledby={titleId || (title ? "modal-default-title" : undefined)} // Link to title
        aria-describedby={descriptionId} // Link to description if provided
        tabIndex={-1} // Make the modal content div focusable for programmatic focus
      >
        {title && !titleId && <h2 id="modal-default-title" className="modal-title">{title}</h2>}
        {/* If titleId is provided, assume the title element with that ID is in children */}

        {showCloseButton && (
          <button 
            type="button" // Important for buttons not submitting forms
            className="modal-close-button" // Styled by Modal.css
            onClick={onClose}
            aria-label="Close modal"
          >
            {/* <Icon name="close" size={24} /> */}
            × {/* HTML entity for 'x' (close icon) */}
          </button>
        )}
        {children}
      </div>
    </div>
  );
};

export default Modal;